import { icons } from "@patient-watch/icons";
import { commonComponents as e } from "@/components/common";
import FooterButtons from "@/components/common/FooterButtons";
import usePatientsQuery from "@/features/profile/api/usePatientsQuery";
import { fuzzySearch } from "@/services/fuzzySearch";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";

// import { PartialPatientT } from "@/@types/SupabaseTypes";
type State = {
    selectedPatient: any;
};

type Action = {
    updateSelectedPatient: (selectedPatient: State["selectedPatient"]) => void;
};

// Create your store, which includes both state and (optionally) actions
export const useSelectedPatientStore = create<State & Action>()(
    devtools(
        persist(
            (set) => ({
                selectedPatient: {},
                updateSelectedPatient: (selectedPatient) => set(() => ({ selectedPatient: selectedPatient })),
            }),
            {
                name: "selected-patient",
            }
        )
    )
);

interface Props {
    nextStage: () => void;
}

const SelectPatient = ({ nextStage }: Props) => {
    const [searchFocused, setSearchFocused] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const searchRef = useRef<any>(null);

    const [selectedPatient, updateSelectedPatient] = useSelectedPatientStore(
        (state) => [state.selectedPatient, state.updateSelectedPatient],
        shallow
    );

    useEffect(() => updateSelectedPatient({}), []);

    const { data: patients } = usePatientsQuery();

    return (
        <div>
            {!searchFocused && (
                <e.Title
                    words="Select Patient" // description="Search for a patient or, if they are not signed up, add a new patient."
                />
            )}
            <section className={`mx-auto flex flex-col items-center justify-center ${searchFocused ? "px-3" : "px-4"}`}>
                <div className="w-full">
                    <div
                        onClick={() => searchRef?.current?.focus()}
                        className={`flex gap-x-2 items-center h-14 bg-white cursor-text form-input`}
                    >
                        <icons.Search className="w-[20px] min-w-[20px] h-[20px] min-h-[20px]" />
                        <input
                            autoFocus
                            onFocus={() => {
                                setSearchFocused(true);
                                updateSelectedPatient({});
                            }}
                            type="text"
                            name="search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name"
                            className="reset-input"
                            ref={searchRef}
                        ></input>
                        {searchFocused && (
                            <button
                                className="ml-auto"
                                onClick={() => {
                                    setSearchFocused(false);
                                    setSearchInput("");
                                    updateSelectedPatient({});
                                }}
                            >
                                <icons.CrossThin fill="black" className="w-[24px] min-w-[24px] h-[24px] min-h-[24px]" />
                            </button>
                        )}
                    </div>
                    {!selectedPatient?.uuid && searchInput && (
                        <>
                            <div className="h-fill">
                                {fuzzySearch(searchInput, "name", patients as any[])?.map((patient: any) => (
                                    <button
                                        key={patient?.uuid}
                                        onClick={() => {
                                            updateSelectedPatient(patient);
                                        }}
                                        className="flex justify-between items-center hover:bg-slate-100 my-4 px-2 py-4 focus:border rounded-xl w-full text-lg"
                                    >
                                        <p>{patient.name}</p>
                                        <icons.BackArrow fill="gray" className="mr-2 w-5 h-5 rotate-180" />
                                    </button>
                                ))}
                            </div>

                            {fuzzySearch(searchInput, "name", patients as any[])?.length === 0 && (
                                <div className="flex justify-between items-center my-4 p-2 text-lg">No Results</div>
                            )}
                        </>
                    )}
                    {selectedPatient?.uuid && (
                        <e.Card className="mt-2">
                            <div className="flex justify-between items-center text-lg">
                                <p className="font-semibold text-gray-800">PATIENT</p>
                                <e.CloseButton close={() => updateSelectedPatient({})} />
                            </div>
                            <div className="flex flex-col gap-y-2 my-2 text-lg">
                                <div>
                                    <label className="font-semibold text-gray-600">Name</label>
                                    <p className="">{selectedPatient?.name}</p>
                                </div>
                                <div>
                                    <label className="font-semibold text-gray-600">Email</label>
                                    <p className="">{selectedPatient?.email}</p>
                                </div>
                                <div>
                                    <label className="font-semibold text-gray-600">NHS Number</label>
                                    <p>{selectedPatient?.nhs_number ? selectedPatient?.nhs_number : "Unknown"}</p>
                                </div>
                                <div>
                                    <label className="font-semibold text-gray-600">Date of Birth</label>
                                    <p>{selectedPatient?.date_of_birth ? selectedPatient?.date_of_birth : "Unknown"}</p>
                                </div>
                            </div>
                        </e.Card>
                    )}
                </div>
                {/* {!searchFocused && (
                    <>
                        <p className="pt-8 font-semibold text-lg">OR</p>
                        <e.Button
                            btn="btn-4"
                            words="Add New Patient"
                            className="font-semibold"
                            href="/check-in/new-patient"
                        />
                    </>
                )} */}
            </section>
            <FooterButtons
                func1={nextStage}
                words1="Next"
                icon1={
                    <div className="flex justify-center items-center ml-[2px] p-[6px] w-8 h-8 rotate-180">
                        <icons.BackArrow fill={selectedPatient?.uuid ? "white" : "gray"} />
                    </div>
                }
                iconSwap1={true}
                disabled1={!selectedPatient?.uuid}
            />
        </div>
    );
};

export const Route = createFileRoute(
    "/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/page/$pageIndex/foot-photos/select-patient"
)({
    component: SelectPatient,
});
